import React from 'react';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import Home from 'lucide-react/dist/esm/icons/home';
import { trackError } from '../../utils/tracking';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorCount: number;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    trackError('React Error Boundary', error.toString(), errorInfo.componentStack ?? null);
    console.error('Error caught by boundary:', error, errorInfo);

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    if (this.state.errorCount > 3) {
      console.error('Too many errors, might need to clear state');
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/dashboard';
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8 bg-surface-page">
          <div className="max-w-lg w-full p-12 rounded-2xl text-center glass-strong">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-status-error/20 flex items-center justify-center">
              <AlertTriangle size={48} className="text-status-error" />
            </div>
            
            <h1 className="text-2xl font-bold text-content mb-4">Oops! Something went wrong</h1>
            <p className="text-secondary mb-8 leading-relaxed">
              We're sorry, but something unexpected happened. 
              {this.props.fallbackMessage || ' Please try refreshing the page.'}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-8 text-left">
                <summary className="cursor-pointer text-sm text-content-secondary hover:text-content mb-2">
                  Error Details (Development Only)
                </summary>
                <pre className="p-4 bg-black/40 rounded-xl text-xs text-status-error-light overflow-auto max-h-48">
                  <strong className="block mb-2">{this.state.error.toString()}</strong>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={this.handleReset} 
                className="inline-flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-br from-brand to-brand-hover border-none rounded-xl text-content-inverse font-semibold cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow"
              >
                <RefreshCw size={18} />
                Try Again
              </button>
              
              <button 
                onClick={this.handleReload} 
                className="inline-flex items-center justify-center gap-2 py-3 px-6 bg-transparent border border-strong rounded-xl text-content font-medium cursor-pointer transition-all duration-200 hover:bg-surface-primary hover:border-strong"
              >
                <RefreshCw size={18} />
                Reload Page
              </button>

              <button 
                onClick={this.handleGoHome} 
                className="inline-flex items-center justify-center gap-2 py-3 px-6 bg-transparent border border-strong rounded-xl text-content font-medium cursor-pointer transition-all duration-200 hover:bg-surface-primary hover:border-strong"
              >
                <Home size={18} />
                Go to Dashboard
              </button>
            </div>

            <p className="mt-8 text-sm text-content-muted">
              If this problem persists, please contact support with the error details.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
